/**
 * SPEC §4.1 / §9.5 — the contribute flow as a visible state machine.
 *
 *   idle ──► permission ──┬──► probing (n/N) ──► submitting ──► submitted
 *                         └──► declined  (terminal: button retires, map unchanged)
 *
 * Nothing here runs before the button is pressed. A denied prompt ends the flow
 * with no request emitted to anyone. Cancelling returns to idle, not declined.
 * Switching provider resets the whole machine to idle (`reset`), including out
 * of `declined`: a new provider is a new offer, with its own disclosure.
 */
import { isRegionProvider, type ObservationResponse, type Provider, type RegionProvider } from '@latency-globe/shared';
import { getProbes, postObservation, ApiError } from './api.ts';
import { locateCell } from './position.ts';
import { connectionAllowsProbing, measureWithFetch, plannedRequests, runProbe, type RunProgress } from './probe.ts';
import type { Turnstile } from './turnstile.ts';

export type ContributeState =
  | { kind: 'idle' }
  | { kind: 'permission' }
  | { kind: 'probing'; provider: RegionProvider; progress: RunProgress }
  | { kind: 'submitting' }
  | { kind: 'submitted'; cell: string; provider: Provider; result: ObservationResponse; code: string | null; rttMs: number | null }
  | { kind: 'inconclusive'; reason: 'all_failed' | 'skipped_connection' }
  | { kind: 'failed'; message: string }
  | { kind: 'declined' };

export class Contribute {
  state: ContributeState = { kind: 'idle' };
  private ctl: AbortController | null = null;
  private listeners = new Set<(s: ContributeState) => void>();

  constructor(private turnstile: Turnstile) {}

  subscribe(fn: (s: ContributeState) => void): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  private set(s: ContributeState): void { this.state = s; for (const fn of this.listeners) fn(s); }

  cancel(): void {
    this.ctl?.abort();
    this.ctl = null;
    if (this.state.kind !== 'declined') this.set({ kind: 'idle' });
  }

  /** Abort anything in flight and offer the button again, whatever the previous outcome. */
  reset(): void {
    this.ctl?.abort();
    this.ctl = null;
    this.set({ kind: 'idle' });
  }

  async start(provider: Provider): Promise<void> {
    if (this.state.kind === 'declined' || this.state.kind === 'permission' || this.state.kind === 'probing' || this.state.kind === 'submitting') return;
    if (isRegionProvider(provider) && !connectionAllowsProbing()) { this.set({ kind: 'inconclusive', reason: 'skipped_connection' }); return; }
    const ctl = new AbortController();
    this.ctl = ctl;
    this.set({ kind: 'permission' });
    const located = await locateCell();
    if (ctl.signal.aborted) return;
    if (located.kind !== 'cell') { this.set({ kind: 'declined' }); return; }
    const cell = located.cell;

    try {
      if (!isRegionProvider(provider)) {
        this.set({ kind: 'submitting' });
        const turnstile = await this.turnstile.token();
        if (ctl.signal.aborted) return;
        const result = await postObservation({ provider: 'cloudflare', cell, turnstile });
        this.set({ kind: 'submitted', cell, provider, result, code: result.accepted ? result.colo : null, rttMs: null });
        return;
      }

      const plan = await getProbes(provider, cell);
      if (ctl.signal.aborted) return;
      this.set({ kind: 'probing', provider, progress: { done: 0, total: plannedRequests(plan.candidates.length, plan.budget), phase: 'warm' } });
      const run = await runProbe(plan.candidates, plan.budget, measureWithFetch, (progress) => {
        if (!ctl.signal.aborted) this.set({ kind: 'probing', provider, progress });
      }, ctl.signal);
      if (ctl.signal.aborted) return;
      if (run.decision.kind !== 'winner') { this.set({ kind: 'inconclusive', reason: run.decision.kind }); return; }

      this.set({ kind: 'submitting' });
      const turnstile = await this.turnstile.token();
      if (ctl.signal.aborted) return;
      const result = await postObservation({
        provider, cell, code: run.decision.code, rtt_ms: Math.round(run.decision.rttMs * 10) / 10,
        confidence: run.decision.confidence, plan_id: plan.plan_id, turnstile,
        ...(run.winnerWarmRank !== null ? { warm_rank: run.winnerWarmRank } : {}),
      });
      this.set({ kind: 'submitted', cell, provider, result, code: run.decision.code, rttMs: run.decision.rttMs });
    } catch (e) {
      if (ctl.signal.aborted) return;
      const message = e instanceof ApiError ? e.message : e instanceof Error && e.message.startsWith('turnstile') ? 'Human verification did not complete.' : 'Something went wrong; nothing was recorded.';
      this.set({ kind: 'failed', message });
    } finally {
      if (this.ctl === ctl) this.ctl = null;
    }
  }
}
